import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useQueryCreditCard } from 'src/queries/creditCard.queries'
// The leaf update module, NOT `creditCardWrites.queries` — that one reaches
// `app/Toast` and closes a store-initialization import cycle for every consumer
// (see `src/CLAUDE.md`); importing it here took down the Accounts routing suite.
import { useUpdateCreditCard } from 'src/queries/creditCards/creditCardUpdate.queries'
import { physicalCardManagementQueries } from 'src/queries/physicalCardManagement/physicalCardManagement.queries'

import { CARD_DETAIL_HERO_DEMO } from './cardDetailHeroDemoData'
import type { CardDetailsAvailability } from './cardDetailHeroMapping'
import {
  canReadCardLimits,
  CardDetailHeroIdentity,
  CardDetailHeroLimits,
  getCardDetailsAvailability,
  toCardDetailHeroIdentity,
  toCardDetailHeroLimits,
} from './cardDetailHeroMapping'

/**
 * Per-group load state. Each group in the hero is fetched independently, so a
 * limits failure must never blank the identity row — that isolation is the
 * whole reason the seam is grouped by *source* rather than by consumer.
 *
 * `unavailable` is distinct from `error` on purpose: a denied
 * `READ_PHYSICAL_CARD_LIMIT` capability is a real answer about this card, not a
 * transient failure the user should retry.
 */
export type CardDetailHeroGroupState =
  | 'loading'
  | 'ready'
  | 'error'
  | 'unavailable'

export type CardDetailHeroData = {
  /** Identity. `undefined` until the credit card detail query resolves. */
  card: CardDetailHeroIdentity | undefined
  cardState: CardDetailHeroGroupState
  /** Limits. `undefined` unless `limitsState` is `ready`. */
  limits: CardDetailHeroLimits | undefined
  limitsState: CardDetailHeroGroupState
  /** Undefined only while the shared capabilities request is still loading. */
  cardDetailsAvailability: CardDetailsAvailability | undefined
  /** A capabilities transport failure fails the reveal affordance closed. */
  cardDetailsCapabilityError: boolean
  /**
   * Persists a new card name through `PUT /creditcards/{id}`. `onSuccess` is a
   * `mutate(...)` callback — UI-only (closing the editor), never invalidation,
   * which lives on the mutation itself so it survives an unmount mid-flight.
   */
  renameCard: (nickname: string, options?: { onSuccess?: () => void }) => void
  renameState: { isPending: boolean; isError: boolean }
}

/**
 * Single data seam for the card detail hero.
 *
 * The return value is grouped by *source*, not by consumer: identity, limits,
 * and capabilities keep independent state and failure windows. Protected card
 * details deliberately do not pass through this query-backed seam.
 *
 * `creditCardId` is a required argument rather than something this hook
 * resolves: the hero is a feature, and features never read routing (the page
 * owns the URL and passes the id down).
 */
export const useCardDetailHeroData = (
  creditCardId: string | undefined,
  /**
   * EX-40596 source selector. False (the default) serves demo limits without
   * consulting the capability or issuing a request, so the Available credit
   * region always shows a figure. The same flag drives the card-details reveal,
   * which the shell reads separately — this hook governs only the limits group.
   * Defaulted rather than required so the identity-only callers need not thread
   * a limits concern.
   */
  cardHeaderLiveDataEnabled = false,
): CardDetailHeroData => {
  // The same key the page already observes (`creditCardQueries.detail`), so
  // React Query dedupes this against it rather than firing a second identity
  // fetch. Do NOT invent a new key or network function for identity.
  const creditCardQuery = useQueryCreditCard({ creditCardId })

  // `retry: false` comes from the factory and is deliberate — see the
  // `unavailable` note above.
  const capabilitiesQuery = useQuery(
    physicalCardManagementQueries.capabilities(creditCardId),
  )

  const limitsAllowed =
    capabilitiesQuery.data !== undefined
      ? canReadCardLimits(capabilitiesQuery.data)
      : undefined
  const cardDetailsAvailability =
    capabilitiesQuery.data !== undefined
      ? getCardDetailsAvailability(capabilitiesQuery.data)
      : undefined

  const limitsQuery = useQuery({
    ...physicalCardManagementQueries.limits(creditCardId),
    // A spread override REPLACES the factory's arg-presence guard rather than
    // merging with it, so it has to be re-stated here. Declarative `enabled`,
    // not an effect: a denied capability must never fire a request that the
    // backend will reject, and the demo path must not request at all.
    enabled:
      cardHeaderLiveDataEnabled &&
      Boolean(creditCardId) &&
      limitsAllowed === true,
  })

  const cardState: CardDetailHeroGroupState =
    creditCardQuery.data !== undefined
      ? 'ready'
      : creditCardQuery.isError
        ? 'error'
        : 'loading'

  const limitsState: CardDetailHeroGroupState =
    ((): CardDetailHeroGroupState => {
      // Demo limits are a constant: nothing to wait for, deny, or fail. The
      // capability describes an endpoint this path never calls, so its verdict
      // — including an error fetching it — must not reach this region.
      if (!cardHeaderLiveDataEnabled) return 'ready'
      if (capabilitiesQuery.isError) return 'error'
      if (limitsAllowed === undefined) return 'loading'
      if (!limitsAllowed) return 'unavailable'
      if (limitsQuery.data !== undefined) return 'ready'
      return limitsQuery.isError ? 'error' : 'loading'
    })()

  const updateCreditCard = useUpdateCreditCard()
  const { mutate: mutateCreditCard } = updateCreditCard
  const creditCard = creditCardQuery.data

  // `PUT /creditcards/{id}` takes the whole card with the address flattened to
  // the top level (the shape the Account Settings boxes already send), so the
  // rename spreads the current card and overrides one field. Invalidation lives
  // on the mutation's own `onSuccess` inside `useUpdateCreditCard` — narrowly,
  // the credit-card detail key only — so it still runs if the hero unmounts
  // mid-flight.
  const renameCard = useCallback(
    (nickname: string, options?: { onSuccess?: () => void }): void => {
      if (creditCard === undefined) return
      mutateCreditCard(
        {
          creditCardId: creditCard.id,
          body: { ...creditCard, ...creditCard.address, displayName: nickname },
        },
        { onSuccess: options?.onSuccess },
      )
    },
    [creditCard, mutateCreditCard],
  )

  return {
    card:
      creditCard !== undefined
        ? toCardDetailHeroIdentity(creditCard)
        : undefined,
    cardState,
    limits: !cardHeaderLiveDataEnabled
      ? CARD_DETAIL_HERO_DEMO.limits
      : limitsState === 'ready' && limitsQuery.data !== undefined
        ? toCardDetailHeroLimits(limitsQuery.data)
        : undefined,
    limitsState,
    cardDetailsAvailability,
    cardDetailsCapabilityError: capabilitiesQuery.isError,
    renameCard,
    renameState: {
      isPending: updateCreditCard.isPending,
      isError: updateCreditCard.isError,
    },
  }
}
